import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CalendarCheck, MapPin, Award, Users, Loader2,
  AlertCircle, ChevronLeft, CheckCircle2, Coffee, Package,
  ShieldCheck, ArrowRight, CreditCard, User, Lock, Upload,
} from "lucide-react";
import {
  useGetPublicEventQuery,
  useRegisterForEventMutation,
  useInitiateEventPaymentMutation,
  useLazyGetMyEventRegistrationsQuery,
} from "../api/eventsApi";
import { submitOnePayForm } from "../../logbook/utils/onepay";
import { useMeQuery } from "../../auth/api/authApi";

const STORAGE_KEY = "logbook_auth_user_v2";

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" });
}
function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? "An error occurred";
}
const MODES = [
  { value: "buffet",   label: "Buffet (Dine-in)" },
  { value: "takeaway", label: "Takeaway"          },
];

type Step = "info" | "payment" | "success";

// A logged-in member auto-maps to the tier whose user_category_id matches their
// grade; guests/others pick a public tier (user_category_id === null).
//
// A grade is priced twice where the event sells a stream as well as a seat, so
// the mode being booked picks between them. Asked for a mode the event does not
// price for that grade, it falls back to the seat rather than leaving a member
// with no price at all.
const autoTierForUser = (tiers: any[], user: any, mode: string = "physical") => {
  const catId = user?.user_category_id;
  if (!catId) return null;
  const mine = tiers.filter((t) => t.user_category_id != null && Number(t.user_category_id) === Number(catId));
  if (!mine.length) return null;
  return mine.find((t) => (t.attendance_mode ?? "physical") === mode)
    ?? mine.find((t) => (t.attendance_mode ?? "physical") === "physical")
    ?? mine[0];
};

/** What a tier costs today. The API already applies the fee timeline. */
const tierAmount = (tier: any) => Number(tier?.current_amount ?? tier?.amount ?? 0);

/**
 * An event's published fees, printed as the approved schedule prints them —
 * every category with its figure, cheapest last is NOT the ordering: the list
 * keeps the order the secretariat entered, because that is the schedule.
 *
 * The viewer's own grade is pulled to the top and set larger. It is the only
 * number on the page they have to act on; the rest is context for whoever is
 * deciding which category they belong to.
 */
function FeeList({
  tiers, heading, autoKey, fmtNaira,
}: {
  tiers: any[];
  heading: string | null;
  autoKey?: string;
  fmtNaira: (n: number) => string;
}) {
  if (!tiers.length) return null;

  const mine = autoKey ? tiers.find((t) => t.category_key === autoKey) : null;
  const rest = mine ? tiers.filter((t) => t.category_key !== mine.category_key) : tiers;

  return (
    <div className="space-y-2">
      {heading && (
        <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{heading}</div>
      )}

      {mine && (
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-black text-gray-900 truncate">{mine.label}</span>
          <span className="text-2xl font-black text-gray-900 shrink-0">{fmtNaira(tierAmount(mine))}</span>
        </div>
      )}

      {rest.map((t) => (
        <div key={t.category_key} className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-gray-500 truncate">
            {/* Labelled "(Virtual)" on save, which is redundant under a Virtual
                heading — drop it so the two columns read as one schedule. */}
            {heading === "Virtual" ? String(t.label).replace(/\s*\(Virtual\)\s*$/i, "") : t.label}
            {t.requires_id_card && (
              <span className="ml-1.5 text-[9px] font-black uppercase tracking-widest text-sky-600">ID</span>
            )}
          </span>
          <span className="text-sm font-bold text-gray-600 shrink-0">{fmtNaira(tierAmount(t))}</span>
        </div>
      ))}
    </div>
  );
}

export default function EventDetailPage() {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();

  const { data: meData }                              = useMeQuery();
  const raw                                           = localStorage.getItem(STORAGE_KEY);
  const cachedUser                                    = raw ? JSON.parse(raw) : null;
  const authUser                                      = meData?.user ?? cachedUser;

  const { data: event, isLoading, isError }           = useGetPublicEventQuery(id!);
  const [register, { isLoading: registering }]        = useRegisterForEventMutation();
  const [initiatePayment, { isLoading: paying }]      = useInitiateEventPaymentMutation();
  const [fetchMyRegistrations]                        = useLazyGetMyEventRegistrationsQuery();

  // ── Form state ─────────────────────────────────────────────────────────────
  const [step, setStep]                               = useState<Step>("info");
  const [attendeeName, setAttendeeName]               = useState("");
  const [attendeeEmail, setAttendeeEmail]             = useState("");
  const [phone, setPhone]                             = useState("");
  const [refreshmentMode, setRefreshmentMode]         = useState("buffet");
  const [categoryKey, setCategoryKey]                 = useState("");
  // In the hall or on the stream. Only asked for where the event prices both.
  const [attendanceMode, setAttendanceMode]           = useState("physical");
  // Proof of the category being claimed, asked for only by tiers that need it.
  const [idCardFile, setIdCardFile]                   = useState<File | null>(null);
  const [refreshmentChoices, setRefreshmentChoices]   = useState<Record<number, number>>({});
  const [formError, setFormError]                     = useState("");

  // Auto-fill from the member's account — name, email and phone all come from
  // the profile, so a logged-in member never re-types their own details.
  useEffect(() => {
    if (authUser) {
      setAttendeeName(authUser.name ?? "");
      setAttendeeEmail(authUser.email ?? "");
      setPhone(authUser.member?.phone ?? authUser.phone_number ?? authUser.phone ?? "");
    }
  }, [authUser]);

  const setChoice = (dayId: number, refId: number) =>
    setRefreshmentChoices((p) => ({ ...p, [dayId]: refId }));

  // ── Validate info step ─────────────────────────────────────────────────────
  const handleInfoNext = () => {
    setFormError("");
    if (!attendeeName.trim())  { setFormError("Full name is required"); return; }
    if (!attendeeEmail.trim()) { setFormError("Email is required"); return; }

    // A tiered, paid event needs a category when it can't be auto-resolved.
    const t: any[] = event?.category_prices ?? [];
    const hasAutoTier = !!autoTierForUser(t, authUser, attendanceMode);
    if (event?.is_paid && t.length > 0 && !hasAutoTier && !categoryKey) {
      setFormError("Please select your registration category.");
      return;
    }

    // Caught here rather than at submit: the server refuses the registration
    // outright, and being told so after the summary screen is a dead end.
    const chosen = t.find((x: any) => x.category_key === (hasAutoTier
      ? autoTierForUser(t, authUser, attendanceMode)?.category_key
      : categoryKey));
    if (chosen?.requires_id_card && !idCardFile) {
      setFormError(`Upload an ID card to register as ${chosen.label}.`);
      return;
    }

    if (event?.has_refreshment) {
      const days: any[] = event.days ?? [];
      for (const day of days) {
        if (!refreshmentChoices[day.id]) {
          setFormError(`Please select a refreshment for ${day.day_label}`);
          return;
        }
      }
    }

    if (event?.is_paid) {
      setStep("payment");
    } else {
      handleSubmit();
    }
  };

  // The registration this member holds for this event, looked up from their own
  // registrations. The safety net for when register/duplicate responses don't
  // carry an id we recognise — the record exists server-side either way, so we
  // must never dead-end the member on "couldn't be paid for".
  const findMyRegistrationId = async (): Promise<number | undefined> => {
    try {
      const mine: any[] = await fetchMyRegistrations().unwrap();
      const match = (mine ?? []).find(
        (r: any) => String(r?.event?.id ?? r?.event_id) === String(event?.id),
      );
      return match?.id;
    } catch {
      return undefined;
    }
  };

  // ── Settle an existing, unpaid registration ────────────────────────────────
  const handlePayExisting = async () => {
    setFormError("");
    const regId = event?.my_registration?.id ?? (await findMyRegistrationId());
    if (!regId) {
      setFormError("Couldn't find your registration. Open it from My Events and use Pay Now.");
      return;
    }

    try {
      const pay: any = await initiatePayment(regId).unwrap();
      if (pay?.already_paid) {
        navigate(`/my-events/${regId}`);
        return;
      }
      if (!pay?.payment_url) {
        throw new Error(pay?.message || "Online payment is unavailable right now. Please try again shortly.");
      }
      submitOnePayForm(pay);
    } catch (err: any) {
      setFormError(err?.message && !err?.data ? err.message : extractError(err));
    }
  };

  // ── Submit registration (then pay online for a paid event) ─────────────────
  const handleSubmit = async () => {
    setFormError("");
    if (!event) return;

    const body: any = {
      attendee_name:    attendeeName.trim(),
      attendee_email:   attendeeEmail.trim(),
      attendee_phone:   phone.trim() || undefined,
      refreshment_mode: refreshmentMode,
    };

    if (event.has_refreshment) {
      body.refreshment_choices = Object.entries(refreshmentChoices).map(([dayId, refId]) => ({
        event_day_id:        Number(dayId),
        event_refreshment_id: refId,
      }));
    }

    // Price tier: member grade auto-applies; otherwise the chosen public tier.
    const tiers = event.category_prices ?? [];
    if (tiers.length) {
      const auto = autoTierForUser(tiers, authUser, attendanceMode);
      const effectiveKey = auto ? auto.category_key : (categoryKey || null);
      if (effectiveKey) body.category_key = effectiveKey;
    }

    // Sent whether or not they were asked: on an event with no virtual seats
    // the server ignores it, and on one with them it decides the fee.
    body.attendance_mode = attendanceMode;

    // A file cannot ride on a JSON body, so a registration carrying an ID card
    // is sent as multipart instead. Built from the SAME object so the two paths
    // can never send different fields.
    let payload: any = body;
    if (idCardFile) {
      const fd = new FormData();
      Object.entries(body).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (k === "refreshment_choices") {
          (v as any[]).forEach((c, i) => {
            fd.append(`refreshment_choices[${i}][event_day_id]`, String(c.event_day_id));
            if (c.event_refreshment_id != null) {
              fd.append(`refreshment_choices[${i}][event_refreshment_id]`, String(c.event_refreshment_id));
            }
          });
          return;
        }
        fd.append(k, String(v));
      });
      fd.append("id_card", idCardFile);
      payload = fd;
    }

    let regId: number | undefined;
    let rawResponse: any;

    try {
      const res: any = await register({ id: event.id, body: payload }).unwrap();
      rawResponse = res;

      // Free event — nothing to pay, we're done.
      if (!event.is_paid) {
        setStep("success");
        return;
      }

      // A paid event ALWAYS goes on to the gateway. Never gate this on a flag
      // from the register response: an API that doesn't send one would silently
      // land the member on "registration successful" without ever paying.
      regId = res?.data?.id ?? res?.data?.registration?.id ?? res?.registration_id ?? res?.id;
    } catch (err: any) {
      // Already registered. That's not fatal while the fee is outstanding — the
      // existing registration is exactly what we want to charge.
      const dup = err?.status === 422 && (err?.data?.registration_id || /already registered/i.test(err?.data?.message ?? ""));
      if (dup) {
        regId = err?.data?.registration_id;
      } else {
        setFormError(extractError(err));
        setStep("info");
        return;
      }
    }

    // The server answered but we couldn't read an id out of it (a non-JSON error
    // body comes back as a "successful" string). Check whether the registration
    // actually landed before deciding what to tell the member.
    if (!regId) regId = await findMyRegistrationId();

    // Paid — hand the member straight over to OnePay. The gateway redirects
    // back to the processing page, which polls until the fee is confirmed.
    try {
      if (!regId) {
        // 2xx but no identifiable registration. Historically this meant the API
        // had emitted stray output before its headers, which forces every
        // response to 200/text/html and hides the real error. Log the body so
        // that class of failure is diagnosable rather than silent.
        // eslint-disable-next-line no-console
        console.error("[event register] unexpected response shape:", rawResponse);
        throw new Error(
          "We couldn't complete your registration — nothing has been charged. Please try again, or contact the secretariat if it keeps happening.",
        );
      }

      const pay: any = await initiatePayment(regId).unwrap();
      if (pay?.already_paid) {
        setStep("success");
        return;
      }
      if (!pay?.payment_url) {
        throw new Error(pay?.message || "Online payment is unavailable right now. Please try again shortly.");
      }

      submitOnePayForm(pay); // leaves the app for the OnePay gateway
    } catch (err: any) {
      setFormError(err?.message && !err?.data ? err.message : extractError(err));
      setStep("info");
    }
  };

  // ── Loading / error states ──────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }
  if (isError || !event) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-rose-50 border border-rose-100 rounded-3xl p-8 flex items-center gap-4">
          <AlertCircle className="w-6 h-6 text-rose-500 shrink-0" />
          <p className="text-rose-700 font-semibold">Event not found or failed to load.</p>
        </div>
      </div>
    );
  }

  const days: any[]        = event.days ?? [];
  const refreshments: any[] = event.refreshments ?? [];

  // ── Price tier resolution ──────────────────────────────────────────────────
  //
  // Every figure on this page is `current_amount` — the tier's fee under the
  // window in force today, which the API resolved. Quoting the tier's normal
  // `amount` would show an early-bird price weeks after it closed.
  const tiers: any[] = event.category_prices ?? [];
  const hasTiers     = tiers.length > 0;
  // Only where the event actually sells a stream is the delegate asked which
  // they are buying.
  const hasVirtual   = tiers.some((t) => t.attendance_mode === "virtual");
  const modeTiers    = hasVirtual
    ? tiers.filter((t) => (t.attendance_mode ?? "physical") === attendanceMode)
    : tiers;
  const autoTier     = autoTierForUser(tiers, authUser, attendanceMode);
  // Public tiers (no membership grade) are the ones guests may choose — in the
  // mode they are booking, so a virtual delegate is not offered a hall seat.
  const publicTiers  = modeTiers.filter((t) => t.user_category_id == null);
  const effectiveKey = autoTier ? autoTier.category_key : (categoryKey || null);
  const selectedTier = hasTiers && effectiveKey ? tiers.find((t) => t.category_key === effectiveKey) : null;
  // The server prices the event for the logged-in member's grade; fall back to
  // resolving it client-side from the tier list.
  const payableAmount = event.is_paid
    ? Number(selectedTier ? tierAmount(selectedTier) : (event.your_price ?? event.price ?? 0))
    : 0;
  const fmtNaira = (n: number) => `₦${Number(n || 0).toLocaleString()}`;
  // Whether the tier they are actually on has to show proof of it.
  const needsIdCard  = !!selectedTier?.requires_id_card;
  // Named so the delegate can see WHY the fee is what it is, and when it moves.
  const windowLabel   = event.has_pricing_timeline ? event.price_window_label : null;
  const nextRise      = event.has_pricing_timeline
    ? (event.price_window === "early_bird" && event.early_bird_ends_at
        ? `Early bird ends ${fmt(event.early_bird_ends_at)}`
        : event.price_window === "normal" && event.late_starts_at
        ? `Late fee applies from ${fmt(event.late_starts_at)}`
        : null)
    : null;

  // ── Existing registration ──────────────────────────────────────────────────
  // One registration per member per event — if they already have one, the form
  // is replaced by its status rather than letting them submit into a 422.
  const myReg          = event.my_registration ?? null;
  const alreadyOnEvent = !!event.is_registered;
  const owesFee        = myReg?.payment_status === "pending";

  // Details that came from the member's own record are shown, not asked for.
  const lockedName  = !!authUser?.name;
  const lockedEmail = !!authUser?.email;
  const lockedPhone = !!(authUser?.member?.phone ?? authUser?.phone_number ?? authUser?.phone);
  const readOnlyCls = "w-full border-b-2 border-gray-100 py-2.5 font-bold text-gray-500 bg-transparent text-sm cursor-not-allowed";

  // ── Success screen ─────────────────────────────────────────────────────────
  if (step === "success") {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div className="bg-white border border-gray-100 rounded-3xl p-10 text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-xl font-black text-gray-900 mb-2">Registration Successful!</h2>
          <p className="text-gray-500 font-medium text-sm mb-6">
            You are registered for <strong>{event.title}</strong>. Check your email for the invoice and access card.
          </p>
          <button
            onClick={() => navigate("/my-events")}
            className="bg-gray-900 text-white font-black text-sm px-8 py-3 rounded-2xl hover:bg-gray-800 transition-colors"
          >
            View My Registrations
          </button>
        </div>
      </div>
    );
  }

  // ── Review & pay (OnePay) ──────────────────────────────────────────────────
  if (step === "payment") {
    const busy = registering || paying;

    return (
      <div className="max-w-2xl mx-auto p-4 md:p-6 pb-20 space-y-6">
        <button
          onClick={() => setStep("info")}
          disabled={busy}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 disabled:opacity-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm space-y-6">
          <div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Step 2 of 2</div>
            <h2 className="text-xl font-black text-gray-900">Confirm &amp; Pay</h2>
            <p className="text-sm text-gray-500 font-medium mt-1">
              Review your details below, then continue to the secure payment gateway.
            </p>
          </div>

          {/* Amount banner */}
          <div className="bg-gray-900 rounded-[1.5rem] p-5 text-white flex items-center justify-between">
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-white/40 mb-1">Amount Due</div>
              <div className="text-2xl font-black">{fmtNaira(payableAmount)}</div>
              {selectedTier && <div className="text-[10px] font-bold text-white/50 mt-0.5">{selectedTier.label}</div>}
            </div>
            <CreditCard className="w-8 h-8 text-white/20" />
          </div>

          {/* Auto-filled member details */}
          <div className="space-y-3">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Your Details</div>
            {[
              { label: "Event",   value: event.title },
              { label: "Name",    value: attendeeName },
              { label: "Email",   value: attendeeEmail },
              ...(phone ? [{ label: "Phone", value: phone }] : []),
              ...(selectedTier ? [{ label: "Category", value: selectedTier.label }] : []),
            ].map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-4 bg-gray-50 rounded-2xl px-4 py-3">
                <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 pt-0.5 shrink-0">
                  {row.label}
                </div>
                <div className="font-bold text-gray-900 text-sm text-right break-words">{row.value}</div>
              </div>
            ))}
          </div>

          {formError && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {formError}
            </div>
          )}

          <button
            onClick={() => handleSubmit()}
            disabled={busy}
            className="w-full bg-gray-900 text-white font-black text-sm py-3.5 rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {busy
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to payment…</>
              : <><CreditCard className="w-4 h-4" /> Pay {fmtNaira(payableAmount)} Now</>}
          </button>

          <div className="flex items-center justify-center gap-2 text-[11px] font-bold text-gray-400">
            <ShieldCheck className="w-3.5 h-3.5" />
            Secured by OnePay — you will be returned here once payment completes.
          </div>
        </div>
      </div>
    );
  }

  // ── Info / registration form ───────────────────────────────────────────────
  const inputCls = "w-full border-b-2 border-gray-100 py-2.5 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  // The fee schedule as the event publishes it, split by what is being bought.
  // Every figure is `current_amount`, so the panel and the checkout can never
  // quote two different numbers.
  const physicalTiers = tiers.filter((t) => (t.attendance_mode ?? "physical") === "physical");
  const virtualTiers  = tiers.filter((t) => t.attendance_mode === "virtual");

  // Registration closes when the event starts — there is no separate close date
  // on an event, and the honest thing is to say the one we do have.
  const opensForRegistration = event.status === "published" && !alreadyOnEvent;

  const scrollToRegister = () => {
    document.getElementById("register")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 pb-20 space-y-6">

      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> All events
      </button>

      {/* Banner */}
      <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-sm">
        {event.background_image ? (
          <img src={event.background_image} alt="" className="w-full h-56 md:h-72 object-cover" />
        ) : (
          <div className="w-full h-56 md:h-72 bg-gradient-to-br from-gray-900 via-indigo-900 to-indigo-700 flex items-center justify-center">
            <CalendarCheck className="w-16 h-16 text-white/20" />
          </div>
        )}
      </div>

      {/* ── Title beside the fee panel ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">

        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-gray-100 text-gray-700">
              {event.hosted_by === "chapter"
                ? `${event.host_chapter?.name ?? "Chapter"} Chapter`
                : event.hosted_by === "institute"
                ? (event.host_institute?.acronym ?? event.host_institute?.name ?? "Accredited Institute")
                : "NIQS National"}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-500">
              <span className={`w-1.5 h-1.5 rounded-full ${hasVirtual ? "bg-sky-500" : "bg-amber-500"}`} />
              {hasVirtual ? "In-Person or Virtual" : "In-Person"}
            </span>
            {event.is_members_only && (
              <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-100">
                Members only
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight tracking-tight uppercase">
            {event.title}
          </h1>
          {event.theme   && <p className="italic text-gray-400 text-sm mt-2">"{event.theme}"</p>}
          {event.tagline && <p className="text-gray-600 font-medium text-base leading-relaxed mt-4">{event.tagline}</p>}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
              <CalendarCheck className="w-4 h-4 text-amber-500 shrink-0" />
              {fmt(event.start_date)}
              {event.end_date !== event.start_date && ` – ${fmt(event.end_date)}`}
            </div>
            {event.location && (
              <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
                <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
                {event.location}
              </div>
            )}
            {event.capacity && (
              <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
                <Users className="w-4 h-4 text-amber-500 shrink-0" />
                {event.capacity} places
              </div>
            )}
          </div>
        </div>

        {/* ── Fee panel ────────────────────────────────────────────────── */}
        <div className="lg:col-span-2 lg:sticky lg:top-6">
          <div className="bg-white border border-gray-100 rounded-3xl shadow-lg shadow-gray-100/70 p-6 space-y-5">

            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-bold text-gray-500">
                {opensForRegistration ? "Registration closes" : "Event date"}
              </span>
              <span className="text-sm font-black text-rose-600">
                {fmt(event.start_date)}
              </span>
            </div>

            {/* An event that charges nothing shows no fee block at all — the
                panel is then just the date and the button. */}
            {!event.is_paid ? null : (
              <div className="border-t border-gray-100 pt-5 space-y-4">
                {windowLabel && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Fees</span>
                    <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-100">
                      {windowLabel} rate
                    </span>
                  </div>
                )}

                <FeeList
                  tiers={physicalTiers}
                  heading={virtualTiers.length ? "In person" : null}
                  autoKey={autoTier?.category_key}
                  fmtNaira={fmtNaira}
                />

                {virtualTiers.length > 0 && (
                  <FeeList
                    tiers={virtualTiers}
                    heading="Virtual"
                    autoKey={autoTier?.category_key}
                    fmtNaira={fmtNaira}
                  />
                )}

                {/* A tiered event with no row for this viewer's grade still has
                    a flat fee — say so rather than showing an empty schedule. */}
                {tiers.length === 0 && (
                  <div className="flex items-baseline justify-between">
                    <span className="font-bold text-gray-700">Registration</span>
                    <span className="text-2xl font-black text-gray-900">{fmtNaira(Number(event.price ?? 0))}</span>
                  </div>
                )}

                {nextRise && (
                  <p className="text-[11px] font-bold text-gray-400 border-t border-gray-100 pt-3">{nextRise}.</p>
                )}
              </div>
            )}

            {alreadyOnEvent ? (
              <button
                onClick={() => navigate(myReg?.id ? `/my-events/${myReg.id}` : "/my-events")}
                className="w-full bg-gray-900 text-white font-black text-sm py-4 rounded-2xl hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
              >
                {owesFee ? "Settle your fee" : "View my registration"} <ArrowRight className="w-4 h-4" />
              </button>
            ) : event.status === "published" ? (
              <button
                onClick={scrollToRegister}
                className="w-full bg-gray-900 text-white font-black text-sm py-4 rounded-2xl hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
              >
                Register now <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <div className="w-full bg-gray-50 border border-gray-100 text-gray-400 font-black text-sm py-4 rounded-2xl text-center">
                Registration closed
              </div>
            )}

            {event.is_cpd && (
              <p className="text-center text-xs font-bold text-gray-400">
                Earns {event.cpd_points} CPD point{Number(event.cpd_points) === 1 ? "" : "s"}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* About */}
      {event.details && (
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">About This Event</div>
          <div className="text-sm text-gray-600 font-medium whitespace-pre-wrap leading-relaxed">{event.details}</div>
        </div>
      )}

      {/* Schedule */}
      {days.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">Schedule</div>
          <div className="space-y-2">
            {days.map((d: any, i: number) => (
              <div key={d.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-xs shrink-0">
                  {d.sort_order ?? i + 1}
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">{d.day_label}</div>
                  <div className="text-xs text-gray-400 font-medium">{fmt(d.day_date)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Already registered — no second registration, just where it stands */}
      {alreadyOnEvent ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm space-y-5">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
              owesFee ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"
            }`}>
              {owesFee ? <CreditCard className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="font-black text-gray-900 text-base">
                {owesFee ? "You're registered — fee outstanding" : "You're already registered"}
              </h2>
              <p className="text-sm text-gray-500 font-medium mt-1">
                {owesFee
                  ? `Your place is held, but the ${fmtNaira(Number(myReg?.amount_paid ?? payableAmount))} fee is still unpaid. Your access card is issued once it clears.`
                  : "You can view your invoice and access card from My Events."}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {owesFee && (
              <button
                onClick={handlePayExisting}
                disabled={paying}
                className="flex items-center gap-2 bg-gray-900 text-white font-black text-sm px-6 py-3 rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {paying
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                  : <><CreditCard className="w-4 h-4" /> Pay {fmtNaira(Number(myReg?.amount_paid ?? payableAmount))} Now</>}
              </button>
            )}
            <button
              onClick={() => navigate(myReg?.id ? `/my-events/${myReg.id}` : "/my-events")}
              className="flex items-center gap-2 border border-gray-200 text-gray-700 font-black text-sm px-6 py-3 rounded-2xl hover:bg-gray-50 transition-colors"
            >
              View My Registration <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {formError && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {formError}
            </div>
          )}
        </div>
      ) : /* Registration form */
      event.status === "published" ? (
        <div id="register" className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm space-y-6 scroll-mt-6">

          {/* Step indicator */}
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              {event.is_paid ? "Step 1 of 2 — " : ""}Registration Details
            </div>
          </div>

          {/* Attendee info — pre-filled from auth */}
          <div className="space-y-4">
            <div className="text-xs font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> Attendee Information
            </div>

            {/* Anything that came from the member's record is displayed, not
                asked for — it must match what the institute has on file. */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Full Name *</label>
              <div className="relative">
                <input
                  className={lockedName ? readOnlyCls : inputCls}
                  value={attendeeName}
                  onChange={(e) => setAttendeeName(e.target.value)}
                  placeholder="Your full name"
                  readOnly={lockedName}
                />
                {lockedName && (
                  <span className="absolute right-0 bottom-2.5 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-gray-400">
                    <Lock className="w-2.5 h-2.5" /> From account
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Email Address *</label>
              <div className="relative">
                <input
                  className={lockedEmail ? readOnlyCls : inputCls}
                  value={attendeeEmail}
                  onChange={(e) => setAttendeeEmail(e.target.value)}
                  placeholder="Your email"
                  type="email"
                  readOnly={lockedEmail}
                />
                {lockedEmail && (
                  <span className="absolute right-0 bottom-2.5 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-gray-400">
                    <Lock className="w-2.5 h-2.5" /> From account
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Phone Number</label>
              <div className="relative">
                <input
                  className={lockedPhone ? readOnlyCls : inputCls}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 08012345678"
                  type="tel"
                  readOnly={lockedPhone}
                />
                {lockedPhone && (
                  <span className="absolute right-0 bottom-2.5 flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-gray-400">
                    <Lock className="w-2.5 h-2.5" /> From account
                  </span>
                )}
              </div>
            </div>

            {(lockedName || lockedEmail || lockedPhone) && (
              <p className="text-[11px] text-gray-400 font-medium">
                These come from your NIQS profile. To change them, update your profile.
              </p>
            )}
          </div>

          {/* Refreshment mode */}
          {event.has_refreshment && (
            <div>
              <div className="text-xs font-black text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Coffee className="w-3.5 h-3.5" /> Refreshment Mode
              </div>
              <div className="flex gap-3">
                {MODES.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setRefreshmentMode(m.value)}
                    className={`flex-1 py-2.5 rounded-2xl border text-sm font-bold transition-colors ${
                      refreshmentMode === m.value
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                        : "border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Per-day refreshment choices */}
          {event.has_refreshment && days.length > 0 && (
            <div className="space-y-4">
              <div className="text-xs font-black text-gray-500 uppercase tracking-widest flex items-center gap-2">
                <Coffee className="w-3.5 h-3.5" /> Refreshment Choices
              </div>
              {days.map((day: any) => {
                const dayRef = refreshments.filter(
                  (r: any) => r.event_day_id === day.id || r.event_day_id === null
                );
                return (
                  <div key={day.id}>
                    <div className="text-xs font-bold text-gray-500 mb-2">
                      {day.day_label} — {fmt(day.day_date)}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {dayRef.map((r: any) => (
                        <button
                          key={r.id}
                          onClick={() => setChoice(day.id, r.id)}
                          className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-colors ${
                            refreshmentChoices[day.id] === r.id
                              ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                              : "border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200"
                          }`}
                        >
                          {r.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Materials notice */}
          {event.has_materials && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl p-4">
              <Package className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs font-medium text-amber-800">
                This event includes materials that will be allocated upon registration and collected on-site.
              </p>
            </div>
          )}

          {/* How they are attending — only where the event sells both */}
          {event.is_paid && hasVirtual && (
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">How are you attending?</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[
                  { value: "physical", label: "In person", hint: "A seat at the venue" },
                  { value: "virtual",  label: "Virtual",   hint: "Join online"          },
                ].map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => { setAttendanceMode(m.value); setCategoryKey(""); }}
                    className={`text-left rounded-2xl px-4 py-3 border transition-colors ${
                      attendanceMode === m.value
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-100 bg-white hover:border-gray-200"
                    }`}
                  >
                    <span className="block text-sm font-bold text-gray-800">{m.label}</span>
                    <span className="block text-[10px] text-gray-400">{m.hint}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Registration category (tiered events) */}
          {event.is_paid && hasTiers && (
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Registration Category</label>
              {autoTier ? (
                <div className="mt-1 flex items-center justify-between bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3">
                  <span className="text-sm font-semibold text-gray-700">{autoTier.label}</span>
                  <span className="text-sm font-black text-gray-900">{fmtNaira(tierAmount(autoTier))}</span>
                </div>
              ) : (
                <select
                  value={categoryKey}
                  onChange={(e) => setCategoryKey(e.target.value)}
                  className="mt-1 w-full border rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-100 bg-white"
                >
                  <option value="">Select your category…</option>
                  {publicTiers.map((t) => (
                    <option key={t.category_key} value={t.category_key}>
                      {t.label} — {fmtNaira(tierAmount(t))}
                    </option>
                  ))}
                </select>
              )}
              {windowLabel && (
                <p className="text-[10px] text-gray-400 mt-1.5">
                  {windowLabel} rate{nextRise ? ` — ${nextRise}` : ""}.
                </p>
              )}
            </div>
          )}

          {/* Proof of the category — only the tiers that ask for it */}
          {needsIdCard && (
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                ID Card
              </label>
              <p className="text-[10px] text-gray-400 mt-0.5 mb-1.5">
                The {selectedTier?.label} rate is only open to those who can show it. Upload your
                ID card — an image or a PDF, up to 4&nbsp;MB.
              </p>
              <label className="flex items-center gap-2 border-2 border-dashed border-gray-200 rounded-2xl px-4 py-3 cursor-pointer hover:border-indigo-400 transition-colors">
                <Upload className="w-4 h-4 text-gray-400 shrink-0" />
                <span className={`text-sm font-medium truncate ${idCardFile ? "text-gray-800" : "text-gray-400"}`}>
                  {idCardFile?.name ?? "Choose file…"}
                </span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => setIdCardFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          )}

          {/* Paid notice */}
          {event.is_paid && (
            <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
              <CreditCard className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
              <p className="text-xs font-medium text-indigo-800">
                This is a paid event — {fmtNaira(payableAmount)}
                {selectedTier ? ` (${selectedTier.label})` : ""}. You will pay securely online via OnePay in the next step.
              </p>
            </div>
          )}

          {formError && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {formError}
            </div>
          )}

          <button
            onClick={handleInfoNext}
            disabled={registering}
            className="w-full bg-gray-900 text-white font-black text-sm py-3.5 rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {registering
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Registering…</>
              : event.is_paid
              ? <><ArrowRight className="w-4 h-4" /> Review &amp; Pay {fmtNaira(payableAmount)}</>
              : <><CheckCircle2 className="w-4 h-4" /> Complete Registration</>}
          </button>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-100 rounded-3xl p-6 text-center">
          <p className="text-gray-400 font-bold text-sm">Registration is not currently open for this event.</p>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <Award className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-1">CPD Information</div>
            <p className="font-bold text-sm text-white/80">
              {event.is_cpd
                ? `Attendees will receive ${event.cpd_points} CPD points upon completion of this event.`
                : "This event does not carry CPD points."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
